import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  ActivityFilters,
  EMPTY_FILTERS,
  type ActivityFilterState,
} from "@/components/ActivityFilters";
import {
  PriceChangeFilters,
  EMPTY_PRICE_CHANGE_FILTERS,
  type PriceChangeFilterState,
} from "@/components/PriceChangeFilters";
import { ActivityDrawer } from "@/components/ActivityDrawer";
import { ActivityTable } from "@/components/ActivityTable";
import { PriceChangesTable } from "@/components/PriceChangesTable";
import {
  api,
  type ActivityItem,
  type ActivityListResponse,
  type PriceChangeListResponse,
} from "@/lib/api";

const PAGE_LIMIT = 100;
const PRICE_CHANGES_LIMIT = 200;

type HistoryTab = "activity" | "price_changes";

interface ActivityListParamsLocal {
  kind?: string;
  marketplace_id?: string;
  success?: boolean;
  since?: string;
  until?: string;
  cursor?: number;
  limit?: number;
}

function buildParams(
  filters: ActivityFilterState,
  cursor: number | undefined,
): ActivityListParamsLocal {
  return {
    kind: filters.kind || undefined,
    marketplace_id: filters.marketplace_id || undefined,
    success: filters.success === "" ? undefined : filters.success === "true",
    since: filters.since ? new Date(filters.since).toISOString() : undefined,
    until: filters.until ? new Date(filters.until).toISOString() : undefined,
    cursor,
    limit: PAGE_LIMIT,
  };
}

export function History(): JSX.Element {
  const qc = useQueryClient();
  // Link dalla Panoramica: `?canale=&tipo=scritti|fallite` apre le variazioni di oggi.
  const [params] = useSearchParams();
  const tipoUrl = params.get("tipo");
  const daPanoramica = tipoUrl === "scritti" || tipoUrl === "fallite";
  const [tab, setTab] = useState<HistoryTab>(daPanoramica ? "price_changes" : "activity");
  const [filters, setFilters] = useState<ActivityFilterState>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<ActivityItem | null>(null);
  const [priceChangeFilters, setPriceChangeFilters] = useState<PriceChangeFilterState>(() =>
    daPanoramica
      ? {
          ...EMPTY_PRICE_CHANGE_FILTERS,
          marketplace_id: params.get("canale") ?? "",
          change_type: tipoUrl === "fallite" ? "failed" : "",
          since: mezzanotteLocale(),
        }
      : EMPTY_PRICE_CHANGE_FILTERS,
  );

  // Paginazione activity
  const activityList = useInfiniteQuery<ActivityListResponse>({
    queryKey: ["activity", filters],
    queryFn: ({ pageParam }) =>
      api.activityList(buildParams(filters, pageParam as number | undefined)),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 5_000,
    enabled: tab === "activity",
  });

  // Paginazione price-changes
  const priceChangesList = useInfiniteQuery<PriceChangeListResponse>({
    queryKey: ["price-changes", priceChangeFilters],
    queryFn: ({ pageParam }) =>
      api.priceChanges({
        marketplace_id: priceChangeFilters.marketplace_id || undefined,
        change_type: priceChangeFilters.change_type || undefined,
        success:
          priceChangeFilters.success === ""
            ? undefined
            : priceChangeFilters.success === "true",
        since: priceChangeFilters.since
          ? new Date(priceChangeFilters.since).toISOString()
          : undefined,
        until: priceChangeFilters.until
          ? new Date(priceChangeFilters.until).toISOString()
          : undefined,
        sku: priceChangeFilters.sku || undefined,
        cursor: pageParam as number | undefined,
        limit: PRICE_CHANGES_LIMIT,
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 5_000,
    enabled: tab === "price_changes",
  });

  // Ricarica top quando arrivano eventi WS `activity_logged`.
  useEffect(() => {
    const handler = (): void => {
      qc.invalidateQueries({ queryKey: ["activity"] });
      qc.invalidateQueries({ queryKey: ["price-changes"] });
    };
    window.addEventListener("am-activity-logged", handler);
    return () => window.removeEventListener("am-activity-logged", handler);
  }, [qc]);

  const allActivityItems = useMemo<ActivityItem[]>(() => {
    const flat = activityList.data?.pages.flatMap((p) => p.items) ?? [];
    if (!filters.search.trim()) return flat;
    const needle = filters.search.trim().toLowerCase();
    return flat.filter((item) => {
      if (item.message.toLowerCase().includes(needle)) return true;
      if (item.sku && item.sku.toLowerCase().includes(needle)) return true;
      return false;
    });
  }, [activityList.data, filters.search]);

  const allPriceChangeItems = useMemo(
    () => priceChangesList.data?.pages.flatMap((p) => p.items) ?? [],
    [priceChangesList.data],
  );

  const totalCount = allActivityItems.length;
  const okCount = allActivityItems.filter((i) => i.success === true).length;
  const koCount = allActivityItems.filter((i) => i.success === false).length;
  const lastTs = allActivityItems[0]?.ts;

  const onExport = (): void => {
    const params = buildParams(filters, undefined);
    const url = api.activityExportUrl({
      kind: params.kind,
      marketplace_id: params.marketplace_id,
      success: params.success,
      since: params.since,
      until: params.until,
    });
    window.open(url, "_blank");
  };

  const onPriceChangeExport = (): void => {
    const url = api.priceChangesExportUrl({
      marketplace_id: priceChangeFilters.marketplace_id || undefined,
      change_type: priceChangeFilters.change_type || undefined,
      success:
        priceChangeFilters.success === ""
          ? undefined
          : priceChangeFilters.success === "true",
      since: priceChangeFilters.since
        ? new Date(priceChangeFilters.since).toISOString()
        : undefined,
      until: priceChangeFilters.until
        ? new Date(priceChangeFilters.until).toISOString()
        : undefined,
      sku: priceChangeFilters.sku || undefined,
    });
    window.open(url, "_blank");
  };

  return (
    <section className="space-y-6 px-7 pb-8 pt-6">
      <header className="space-y-1">
        <h1 className="page-title">
          Storico attività
        </h1>
        <p className="page-subtitle">
          Tutto quello che è successo: aggiornamenti, prezzi scritti in automatico o a mano,
          errori e modifiche alle impostazioni.
        </p>
      </header>

      {/* Tab selector */}
      <div className="flex gap-1 border-b border-line">
        <button
          type="button"
          onClick={() => setTab("activity")}
          className={
            tab === "activity"
              ? "-mb-px border-b-2 border-ink px-4 pb-2 pt-1 text-md font-semibold text-ink"
              : "px-4 pb-2 pt-1 text-md text-ink-3 transition hover:text-ink"
          }
        >
          Tutte le attività
        </button>
        <button
          type="button"
          onClick={() => setTab("price_changes")}
          className={
            tab === "price_changes"
              ? "-mb-px border-b-2 border-ink px-4 pb-2 pt-1 text-md font-semibold text-ink"
              : "px-4 pb-2 pt-1 text-md text-ink-3 transition hover:text-ink"
          }
        >
          Variazioni prezzo
        </button>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* TAB: Tutte le attività                                               */}
      {/* ------------------------------------------------------------------ */}
      {tab === "activity" && (
        <>
          <ActivityFilters
            state={filters}
            onChange={setFilters}
            onReset={() => setFilters(EMPTY_FILTERS)}
            onExport={onExport}
          />

          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 px-1 text-md tabular-nums text-ink-2">
            <span>
              <strong className="text-ink">{totalCount}</strong> attività
            </span>
            <span className="flex items-center gap-1.5">
              <span className="dot dot-ok" aria-hidden="true" />
              <strong className="text-ink">{okCount}</strong> riuscite
            </span>
            <span className="flex items-center gap-1.5">
              <span className="dot dot-err" aria-hidden="true" />
              <strong className={koCount > 0 ? "text-danger" : "text-ink"}>{koCount}</strong> fallite
            </span>
            {lastTs && (
              <span className="text-label text-ink-3">
                ultima: {new Date(lastTs).toLocaleString("it-IT")}
              </span>
            )}
          </div>

          {activityList.isError && (
            <div className="rounded-[var(--radius)] border border-destructive/35 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              Errore caricamento storico:{" "}
              {activityList.error instanceof Error
                ? activityList.error.message
                : "richiesta fallita"}
            </div>
          )}

          <ActivityTable items={allActivityItems} onRowClick={setSelected} />

          {activityList.hasNextPage && (
            <div className="flex justify-center">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={activityList.isFetchingNextPage}
                onClick={() => activityList.fetchNextPage()}
              >
                {activityList.isFetchingNextPage ? "Caricamento…" : "Carica altri"}
              </button>
            </div>
          )}

          <ActivityDrawer item={selected} onClose={() => setSelected(null)} />
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* TAB: Variazioni prezzo                                               */}
      {/* ------------------------------------------------------------------ */}
      {tab === "price_changes" && (
        <>
          <PriceChangeFilters
            state={priceChangeFilters}
            onChange={setPriceChangeFilters}
            onReset={() => setPriceChangeFilters(EMPTY_PRICE_CHANGE_FILTERS)}
            onExport={onPriceChangeExport}
          />

          <div className="px-1 text-md tabular-nums text-ink-2">
            <strong className="text-ink">{allPriceChangeItems.length}</strong> variazioni
          </div>

          {priceChangesList.isError && (
            <div className="rounded-[var(--radius)] border border-destructive/35 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              Errore caricamento variazioni:{" "}
              {priceChangesList.error instanceof Error
                ? priceChangesList.error.message
                : "richiesta fallita"}
            </div>
          )}

          <PriceChangesTable items={allPriceChangeItems} />

          {priceChangesList.hasNextPage && (
            <div className="flex justify-center">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={priceChangesList.isFetchingNextPage}
                onClick={() => priceChangesList.fetchNextPage()}
              >
                {priceChangesList.isFetchingNextPage ? "Caricamento…" : "Carica altri"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// Valore per <input type="datetime-local">: mezzanotte di oggi nel fuso del browser.
function mezzanotteLocale(): string {
  const oggi = new Date();
  const due = (n: number) => String(n).padStart(2, "0");
  return `${oggi.getFullYear()}-${due(oggi.getMonth() + 1)}-${due(oggi.getDate())}T00:00`;
}
